import { SizeType } from 'antd/es/config-provider/SizeContext';
import * as React from 'react';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { ExpressionType, IExpression } from '../../../IExpression';
import { isObjectsArrayField } from '../../../datatype/Datatype';
import { IReference } from '../../../references/IReference';
import { configLogger } from '../../../utils/configLogger';
import { ConditionsListInput } from '../../condition/input/ConditionsListInput';
import { SubconditionGroupExpressionSchema } from '../SubconditionsGroupExpression.type';

export interface ISubconditionsGroupInputProps {
  name: string;
  conditionReference: IReference;
  size?: SizeType;
}

const $NestedConditionsWrapper = styled.div`
  padding: 8px;
  padding-top: 0;
  margin-left: 8px;
  border-radius: ${props => props.theme.borderRadiusSM}px;
  border-top-left-radius: 0;
  background-color: ${props => props.theme.blue1};
  width: auto;
`;

export function SubconditionsGroupInput(props: ISubconditionsGroupInputProps) {
  const { name, size, conditionReference } = props;

  if (!isObjectsArrayField(conditionReference.field)) {
    throw new Error('SubconditionsGroupInput: conditionReference.field is not an ObjectsArrayField'); // prettier-ignore
  }

  const methods = useFormContext();

  const value = useWatch({
    name: name,
  }) as IExpression;
  const type = value?.type as IExpression['type'];

  /**
   * Quando si passa da un'Assignable (es >, <, =, ecc.) a un operatore
   * "Almeno uno" di una Lista, cambia il componente che fa scegliere le regole.
   * Potrebbe esserci un valore configurato prima, es. una `ValueExpression`;
   * in quel caso, dobbiamo resettarla.
   */
  useEffect(() => {
    if (!type || type !== ExpressionType.SubconditionsGroup) {
      const nextValue = SubconditionGroupExpressionSchema.cast(
        // Attenzione, non possiamo passare il `value` precedente perché
        // nel fare il cast non è detto che yup rimuova tutti i campi ecc.
        // Per sicurezza è più comodo generare un oggetto _nuovo_, dato che
        // comunque la struttura è diversa.
        {},
        { assert: false },
      );
      configLogger.debug(`[SubconditionsGroup.reset]`, { type, nextValue }); // prettier-ignore
      methods.setValue(name, nextValue, { shouldValidate: false });
    }
  }, [type, name, methods, value]);

  return (
    <$NestedConditionsWrapper>
      <ConditionsListInput
        size={size ?? 'middle'}
        name={`${name}.conditions`}
        references={conditionReference.field.structure}
        nested
      />
    </$NestedConditionsWrapper>
  );
}
